/**
 * <TutorAnnotationToolbar> — dessiner sur ce qu'on présente (molecule).
 *
 * Pendant « Présenter », l'ancienne fiche en live (`TodoModal`, groupe segmenté) remplaçait son bouton
 * par quatre gestes : le crayon (« Dessiner (P) », puis « Désactiver (Échap) »), « Annuler (Ctrl+Z) »,
 * « Tout effacer (Suppr) » et « Arrêter ». Les voici, dans le même ordre : le crayon prend l'aplat
 * choisi de l'univers quand il dessine, annuler se tait quand il n'y a rien à annuler, et « Arrêter »
 * est le rouge de ce qui coupe. Les raccourcis du clavier (web) sont un effet de l'écran ; ils se
 * disent dans le nom des boutons.
 *
 * Le tableau blanc prend la même barre (round 3) : le crayon y est toujours là, donc ni crayon ni
 * « Arrêter » (`onToggleDraw` et `onStop` absents), et deux gestes s'ajoutent, que la présentation peut
 * aussi prendre : **la couleur du trait** (quatre encres, `TUTOR_INKS`, `pen`). Les encres ne se montrent
 * que quand le crayon dessine (toujours sur le tableau blanc). **Pas de gomme** : « annuler la dernière
 * action » corrige un trait (Wajih, live en prod du 03/11 ; la gomme reviendra plus tard).
 *
 * **Le visuel du tableau blanc, pendant une présentation** (`visual`) : la bascule « Visuel » (le montrer ou
 * le cacher sur le tableau) et l'avis du masteur sur lui (deux pouces, tant qu'il est montré). L'ancien
 * `WhiteboardTab` (`FloatingControls`, `FeedbackRow`) les gardait sur le tableau pendant la présentation ;
 * ici le tableau est ce qui part chez les élèves, la barre de dessin est la place des gestes du masteur.
 * Absent (défaut) : la barre d'avant.
 *
 * **« Générer un visuel »** (`generateVisual`, le tableau blanc) : posé au milieu du tableau, il gênait le
 * dessin (live du 04/11) ; il est un geste de la barre, qui tourne pendant la préparation et propose de
 * réessayer quand le visuel n'a pas pu être fait. Une fois le visuel prêt, la bascule « Visuel » le remplace.
 *
 * Contrôlée : `drawing`, `canUndo`, l'encre et le visuel arrivent en props, chaque geste repart.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UICorrectionFeedback } from "../../../ports/primary/ui/types/studentActivity"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { type SemanticColorKey } from "../../theme/tokens/colors"

/** Les quatre encres du trait : le marine, le rouge de la correction, le vert, le teal du tuteur. */
export const TUTOR_INKS = ["primary", "danger", "success", "role"] as const satisfies readonly SemanticColorKey[]
export type TutorInk = (typeof TUTOR_INKS)[number]

/** Le trait : son encre. */
export type TutorAnnotationPen = {
    ink: TutorInk
    onChangeInk: (ink: TutorInk) => void
}

/**
 * Le visuel du tableau blanc dans la barre : montré ou caché, et l'avis du masteur (`feedback`, absent : pas
 * de pouces ; `value` à `null` tant qu'il n'a rien dit).
 */
export type TutorAnnotationVisual = {
    shown: boolean
    onToggle: () => void
    feedback?: { value: UICorrectionFeedback | null; onChange: (value: UICorrectionFeedback) => void }
}

export type TutorAnnotationToolbarProps = {
    /** Le crayon : absent sur le tableau blanc, où l'on dessine toujours. */
    drawing?: boolean
    onToggleDraw?: () => void
    canUndo?: boolean
    onUndo: () => void
    onClear: () => void
    /** « Arrêter » la présentation. Absent : pas de bouton (le tableau blanc). */
    onStop?: () => void
    /** L'encre. Absent : le trait de toujours. */
    pen?: TutorAnnotationPen
    /** L'arrêt du partage part. Défaut `false`. */
    stopping?: boolean
    /** Le visuel du tableau blanc, avant « Arrêter ». Absent (défaut) : rien. */
    visual?: TutorAnnotationVisual
    /**
     * « Générer un visuel » (`pending` : il se prépare ; `retry` : il n'a pas pu être fait). Absent (défaut) : rien.
     */
    generateVisual?: { onPress: () => void; pending?: boolean; retry?: boolean }
    testID?: string
}

const TEXTS = {
    draw: "Dessiner (P)",
    stopDraw: "Désactiver (Échap)",
    undo: "Annuler (Ctrl+Z)",
    clear: "Tout effacer (Suppr)",
    stop: "Arrêter",
    stopLabel: "Arrêter la présentation",
    inks: "Couleur du trait",
    visual: "Visuel",
    visualShow: "Montrer le visuel sur le tableau",
    visualHide: "Cacher le visuel du tableau",
    visualUseful: "Visuel utile",
    visualNotUseful: "Visuel peu utile",
    generateVisual: "Générer un visuel",
    generating: "Visuel en préparation",
    retryVisual: "Réessayer le visuel",
    ink: {
        primary: "Encre marine",
        danger: "Encre rouge",
        success: "Encre verte",
        role: "Encre bleu canard",
    } satisfies Record<TutorInk, string>,
}

const SIZE = 32
const INK_SIZE = 18

export function TutorAnnotationToolbar(props: TutorAnnotationToolbarProps) {
    const {
        drawing = false,
        onToggleDraw,
        canUndo = true,
        onUndo,
        onClear,
        onStop,
        pen,
        stopping = false,
        visual,
        generateVisual,
        testID,
    } = props
    const feedback = visual?.feedback
    return (
        <View style={styles.root} testID={testID}>
            {onToggleDraw != null && (
                <IconButton
                    iconName="pencil-alt"
                    color={drawing ? "role-selected" : "surface-subtle"}
                    iconColor={drawing ? "text-on-role" : "primary"}
                    shape="square"
                    size={SIZE}
                    iconSize={16}
                    selected={drawing}
                    onPress={onToggleDraw}
                    accessibilityLabel={drawing ? TEXTS.stopDraw : TEXTS.draw}
                />
            )}
            {/* Les encres, quand le crayon dessine (sur le tableau blanc, il dessine toujours). */}
            {pen != null && (onToggleDraw == null || drawing) && (
                <View accessibilityRole="radiogroup" accessibilityLabel={TEXTS.inks} style={styles.inks}>
                    {TUTOR_INKS.map((ink) => {
                        const chosen = pen.ink === ink
                        return (
                            <Pressable
                                key={ink}
                                onPress={() => pen.onChangeInk(ink)}
                                accessibilityRole="radio"
                                accessibilityState={{ checked: chosen }}
                                accessibilityLabel={TEXTS.ink[ink]}
                                style={styles.inkTarget}
                            >
                                <View style={styles.ink(ink, chosen)} />
                            </Pressable>
                        )
                    })}
                </View>
            )}
            <IconButton
                iconName="undo-alt"
                color="surface-subtle"
                iconColor="primary"
                shape="square"
                size={SIZE}
                iconSize={16}
                disabled={!canUndo}
                onPress={onUndo}
                accessibilityLabel={TEXTS.undo}
            />
            <IconButton
                iconName="trash"
                color="surface-subtle"
                iconColor="primary"
                shape="square"
                size={SIZE}
                iconSize={16}
                onPress={onClear}
                accessibilityLabel={TEXTS.clear}
            />
            {generateVisual != null && (
                <Button
                    label={generateVisual.retry === true ? TEXTS.retryVisual : TEXTS.generateVisual}
                    accessibilityLabel={generateVisual.pending === true ? TEXTS.generating : undefined}
                    iconName={generateVisual.retry === true ? "redo" : "magic"}
                    variant="outline"
                    size="sm"
                    loading={generateVisual.pending === true}
                    onPress={generateVisual.onPress}
                />
            )}
            {visual != null && (
                <ChoiceChip
                    label={TEXTS.visual}
                    iconName={visual.shown ? "eye" : "eye-slash"}
                    selected={visual.shown}
                    onPress={visual.onToggle}
                    accessibilityLabel={visual.shown ? TEXTS.visualHide : TEXTS.visualShow}
                />
            )}
            {/* L'avis, tant que le visuel est montré (l'ancien `FeedbackRow` sous l'image). */}
            {visual?.shown === true && feedback != null && (
                <>
                    <IconButton
                        iconName="thumbs-up"
                        shape="square"
                        size={SIZE}
                        iconSize={16}
                        appearance={feedback.value === "positive" ? "filled" : "transparent"}
                        color="successLight"
                        iconColor={feedback.value === "positive" ? "success-strong" : "text-muted"}
                        selected={feedback.value === "positive"}
                        onPress={() => feedback.onChange("positive")}
                        accessibilityLabel={TEXTS.visualUseful}
                    />
                    <IconButton
                        iconName="thumbs-down"
                        shape="square"
                        size={SIZE}
                        iconSize={16}
                        appearance={feedback.value === "negative" ? "filled" : "transparent"}
                        color="dangerLight"
                        iconColor={feedback.value === "negative" ? "danger-strong" : "text-muted"}
                        selected={feedback.value === "negative"}
                        onPress={() => feedback.onChange("negative")}
                        accessibilityLabel={TEXTS.visualNotUseful}
                    />
                </>
            )}
            {onStop != null && (
                <Button
                    label={TEXTS.stop}
                    iconName="chalkboard-teacher"
                    color="danger"
                    size="sm"
                    loading={stopping}
                    onPress={onStop}
                    accessibilityLabel={TEXTS.stopLabel}
                />
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flexDirection: "row", alignItems: "center", gap: theme.spacing["1"] },
    inks: { flexDirection: "row", alignItems: "center" },
    inkTarget: { width: SIZE, height: SIZE, alignItems: "center" as const, justifyContent: "center" as const },
    /** Une encre : une pastille pleine, cerclée quand elle est choisie. */
    ink: (ink: TutorInk, chosen: boolean) => ({
        width: INK_SIZE,
        height: INK_SIZE,
        borderRadius: INK_SIZE / 2,
        backgroundColor: theme.colors[ink],
        borderWidth: chosen ? 2 : 0,
        borderColor: theme.colors["surface"],
        outlineWidth: chosen ? 2 : 0,
        outlineColor: theme.colors[ink],
        outlineStyle: "solid" as const,
    }),
}))
